import { syncVariants } from './variantSync';

/**
 * ฟังก์ชันจัดการ เพิ่ม/ลบ Variants ตามการเลือก Option จากหน้าจอ
 * รับ baseName และ baseCode เพิ่มเข้ามา
 */
export function handleVariantChange(currentVariants, changePayload, baseName, baseCode, baseServingSizeUnitId, baseUnitId, baseNameEn = '') {
  // รับ name / name_en และ code จาก payload ที่ส่งมาจาก AttributeForm
  const { id, attribute_id, selected, name, name_en, code } = changePayload;
  const optionId = parseInt(id);
  const attributeId = parseInt(attribute_id);

  // 1. แกะโครงสร้าง Attributes จาก Variants เดิมที่มีอยู่ (Reverse Engineering)
  // เพื่อให้รู้ว่าตอนนี้ระบบมี Config อะไรบ้าง (รวมถึงชื่อ Option เดิมที่เคยมี)
  let attributesConfig = extractAttributesFromVariants(currentVariants);

  // 2. อัปเดตโครงสร้าง (เพิ่ม/ลบ Option) พร้อมส่ง name/code เข้าไปเก็บ
  attributesConfig = updateConfig(attributesConfig, attributeId, optionId, selected, name, code, name_en);

  // 3. Gen Variants ใหม่ ส่ง baseName, baseCode ไปด้วย
  return syncVariants(currentVariants, attributesConfig, baseName, baseCode, baseServingSizeUnitId, baseUnitId, baseNameEn);
}

/**
 * ฟังก์ชันจัดการเมื่อมีการ "ลบ Attribute" หรือ "เปลี่ยน Attribute ตัวแม่"
 * ฟังก์ชันนี้จะล้าง Options ทั้งหมดของ Attribute นั้นออกจาก Variants
 */
export function handleAttributeRemoval(currentVariants, attributeIdToRemove, baseName, baseCode, baseServingSizeUnitId, baseUnitId, baseNameEn = '') {
  const idToRemove = parseInt(attributeIdToRemove);
  if (!idToRemove) return currentVariants;

  // 1. แกะโครงสร้าง Attributes เดิม
  let attributesConfig = extractAttributesFromVariants(currentVariants);

  // 2. กรองเอา Attribute ที่ต้องการลบออกไปเลย
  attributesConfig = attributesConfig.filter(attr => attr.id !== idToRemove);

  // 3. Gen Variants ใหม่ (Variants จะลดลง หรือ Merge กัน)
  return syncVariants(currentVariants, attributesConfig, baseName, baseCode, baseServingSizeUnitId, baseUnitId, baseNameEn);
}

/**
 * แปลง Variants กลับเป็น Config Structure
 * Output: [ { id: 1, options: [{attribute_option_id: 1, name: '...', code: '...'}, ...] }, ... ]
 */
function extractAttributesFromVariants(variants) {
  const configMap = new Map();
  variants.forEach(variant => {
    if (!variant.attributes) return;
    variant.attributes.forEach(attr => {
      if (!configMap.has(attr.id)) {
        configMap.set(attr.id, new Map()); // ใช้ Map ซ้อน Map เพื่อเก็บ Option Object แบบไม่ซ้ำ
      }
      const optionMap = configMap.get(attr.id);
      // เก็บ Option Object ทั้งก้อน (รวม name, code ที่เคยเซฟไว้ใน variant attributes)
      if (!optionMap.has(attr.option_id)) {
        optionMap.set(attr.option_id, {
          attribute_option_id: attr.option_id,
          name: attr.option_name, // ดึง name เก่าคืนมา
          name_en: attr.option_name_en, // ดึงชื่ออังกฤษเก่าคืนมา
          code: attr.option_code  // ดึง code เก่าคืนมา
        });
      }
    });
  });

  // แปลง Map กลับเป็น Array Format
  const config = [];
  for (const [attrId, optionMap] of configMap.entries()) {
    const options = Array.from(optionMap.values());
    // เรียงลำดับ Option ตาม ID
    options.sort((a, b) => a.attribute_option_id - b.attribute_option_id);
    config.push({ id: attrId, options: options });
  }

  // เรียงลำดับ Attribute ตาม ID
  // config.sort((a, b) => a.id - b.id);
  return config;
}

/**
 * เพิ่ม หรือ ลบ Option ออกจาก Config Array
 */
function updateConfig(config, attrId, optId, isSelected, optName, optCode, optNameEn = '') {
  const newConfig = JSON.parse(JSON.stringify(config)); // Deep Clone กันค่าเพี้ยน
  let attribute = newConfig.find(a => a.id === attrId);

  if (isSelected) {
    // === กรณีเพิ่ม (Add) ===
    if (!attribute) {
      // ถ้าเป็น Attribute ใหม่ที่ไม่เคยมี
      attribute = { id: attrId, options: [] };
      newConfig.push(attribute);
    }

    const exists = attribute.options.find(o => o.attribute_option_id === optId);

    // ข้อมูล Option ที่จะเพิ่ม/อัปเดต
    const optionData = {
      attribute_option_id: optId,
      name: optName, // เก็บชื่อ (เช่น "หมู")
      name_en: optNameEn, // เก็บชื่ออังกฤษ (เช่น "Pork")
      code: optCode  // เก็บรหัส (เช่น "PORK")
    };

    if (!exists) {
      attribute.options.push(optionData);
    } else {
      // อัปเดตข้อมูล name/code ถ้ามีอยู่แล้ว (เผื่อกรณี User ไปแก้ชื่อ Option ใน Master)
      Object.assign(exists, optionData);
    }
  } else {
    // === กรณีลบ (Remove) ===
    if (attribute) {
      attribute.options = attribute.options.filter(o => o.attribute_option_id !== optId);

      // ถ้า Attribute นั้นไม่เหลือ Option เลย -> ให้ลบ Attribute นั้นทิ้งไปเลย
      if (attribute.options.length === 0) {
        return newConfig.filter(a => a.id !== attrId);
      }
    }
  }
  return newConfig;
}
